// Public comparison landing pages — Treasurescare vs AppleCare / SamsungCare
// Same template used for both routes; `provider` prop switches copy + prices.
import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { ShieldCheck, Check, X, Sparkles, ChevronRight, Zap, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";

// Rows: [feature, treasurescare, competitor]  · true=Check, false=X, string=inline value
const COMPARISON_ROWS = {
  applecare: [
    { feature: "Monthly cost (flagship)", ts: "£7.99 – £14.99", them: "£11.49 – £14.99", win: true },
    { feature: "2-year total (2 screen + 1 damage)", ts: "£163", them: "£344 – £369", win: true },
    { feature: "Excess on screen repair", ts: "£0 on Hunter+", them: "£29 every time", win: true },
    { feature: "Excess on other damage", ts: "£0 on Vault+", them: "£99 every time", win: true },
    { feature: "Turnaround time", ts: "24h avg", them: "5 – 7 days", win: true },
    { feature: "AI damage pre-check before you claim", ts: true, them: false, win: true },
    { feature: "Covers all brands (iPhone + Samsung + Google…)", ts: true, them: false, win: true },
    { feature: "Multi-device family bundle discount", ts: "15% off at 3+ devices", them: "None", win: true },
    { feature: "Referral rewards", ts: "Give a month, get a month", them: "None", win: true },
    { feature: "Theft & loss cover", ts: "Yes — Vault + Household", them: "Yes — extra tier", win: false },
    { feature: "Battery service < 80% capacity", ts: true, them: true, win: null },
    { feature: "Global repair coverage", ts: "Yes — Household", them: true, win: null },
    { feature: "24/7 support", ts: true, them: true, win: null },
  ],
  samsungcare: [
    { feature: "Monthly cost (flagship)", ts: "£7.99 – £14.99", them: "£10 – £15", win: true },
    { feature: "2-year total (2 screen + 1 damage)", ts: "£163", them: "£329 – £361", win: true },
    { feature: "Excess on screen repair", ts: "£0 on Hunter+", them: "£29 every time", win: true },
    { feature: "Excess on other damage", ts: "£0 on Vault+", them: "£99 every time", win: true },
    { feature: "Turnaround time", ts: "24h avg", them: "1 – 3 days (in-store)", win: true },
    { feature: "AI damage pre-check before you claim", ts: true, them: false, win: true },
    { feature: "Covers all brands (Samsung + iPhone + Google…)", ts: true, them: false, win: true },
    { feature: "Multi-device family bundle discount", ts: "15% off at 3+ devices", them: "None", win: true },
    { feature: "Referral rewards", ts: "Give a month, get a month", them: "None", win: true },
    { feature: "Theft & loss cover", ts: "Yes — Vault + Household", them: "Yes — extra tier", win: false },
    { feature: "Same-day screen repair (walk-in)", ts: false, them: true, win: null },
    { feature: "Unlimited accidental damage claims", ts: "Yes — Vault+", them: true, win: null },
    { feature: "24/7 support", ts: true, them: true, win: null },
  ],
};

const PROVIDER_META = {
  applecare: { name: "AppleCare+", brand: "Apple", accent: "text-[#8E8E93]" },
  samsungcare: { name: "Samsung Care+", brand: "Samsung", accent: "text-[#1428A0]" },
};

function CellBool({ v }) {
  if (v === true) return <Check className="w-5 h-5 text-primary" />;
  if (v === false) return <X className="w-5 h-5 text-destructive/70" />;
  return <span className="text-sm">{v}</span>;
}

export default function ComparePage({ provider = "applecare" }) {
  const meta = PROVIDER_META[provider];
  const rows = COMPARISON_ROWS[provider];
  const nav = useNavigate();
  const wins = rows.filter((r) => r.win === true).length;

  return (
    <div className="min-h-screen bg-background" data-testid={`compare-${provider}-page`}>
      <header className="max-w-5xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5">
          <div className="w-10 h-10 rounded-xl bg-primary flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-secondary" />
          </div>
          <div className="font-display font-semibold text-lg">Treasurescare</div>
        </Link>
        <Link to="/" className="text-sm text-muted-foreground hover:text-foreground">
          &larr; back to home
        </Link>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-12">
        <div className="text-center max-w-2xl mx-auto">
          <div className="overline text-secondary flex items-center justify-center gap-1">
            <Sparkles className="w-3 h-3" /> Independent comparison
          </div>
          <h1 className="mt-3 font-display text-4xl md:text-6xl font-light tracking-tighter">
            Treasurescare <span className="italic text-primary">vs</span> {meta.name}
          </h1>
          <p className="mt-4 text-muted-foreground text-lg">
            Same {meta.brand} phone. Half the total cost. Zero excess on standard repairs.
            Here's exactly how we stack up.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 rounded-full bg-primary/10 border border-primary/30 px-4 py-2 text-sm text-primary">
            <Sparkles className="w-4 h-4" /> Treasurescare wins on <strong>{wins} of {rows.length}</strong> criteria
          </div>
        </div>

        {/* Hero cost snapshot */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div
            className="rounded-3xl border border-primary/30 bg-primary/5 p-8 relative overflow-hidden"
            data-testid="compare-treasurescare-cost"
          >
            <div className="overline text-primary">Treasurescare Hunter · 2 years</div>
            <div className="mt-3 font-display text-6xl font-semibold text-primary">£163</div>
            <div className="text-sm text-muted-foreground mt-1">Everything included · £0 excess</div>
            <div className="mt-6 flex items-center gap-2 text-sm text-foreground/80">
              <Check className="w-4 h-4 text-primary shrink-0" /> Screen + battery + water
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-foreground/80">
              <Check className="w-4 h-4 text-primary shrink-0" /> AI pre-check · 24h TAT
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-foreground/80">
              <Check className="w-4 h-4 text-primary shrink-0" /> Works on iPhone AND Samsung
            </div>
          </div>
          <div
            className={`rounded-3xl border border-destructive/20 bg-destructive/5 p-8 ${meta.accent}`}
            data-testid={`compare-${provider}-cost`}
          >
            <div className="overline text-destructive">{meta.name} · 2 years</div>
            <div className="mt-3 font-display text-6xl font-semibold text-destructive">
              £{provider === "applecare" ? "344" : "329"}+
            </div>
            <div className="text-sm text-muted-foreground mt-1">Plus £29 per screen + £99 per damage claim</div>
            <div className="mt-6 flex items-center gap-2 text-sm text-foreground/80">
              <X className="w-4 h-4 text-destructive/70 shrink-0" /> Excess every single claim
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-foreground/80">
              <X className="w-4 h-4 text-destructive/70 shrink-0" /> One brand only
            </div>
            <div className="mt-1 flex items-center gap-2 text-sm text-foreground/80">
              <X className="w-4 h-4 text-destructive/70 shrink-0" /> No family bundle · no referrals
            </div>
          </div>
        </div>

        {/* Detailed row-by-row table */}
        <div className="mt-12 bg-card border border-border rounded-3xl overflow-hidden" data-testid="compare-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-muted/40 text-muted-foreground">
                <th className="text-left px-5 py-4">Feature</th>
                <th className="text-center px-5 py-4 text-primary">Treasurescare</th>
                <th className="text-center px-5 py-4">{meta.name}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, i) => (
                <tr key={r.feature} className={`border-t border-border ${r.win === true ? "bg-primary/[0.03]" : ""}`}>
                  <td className="px-5 py-3 text-foreground/90">{r.feature}</td>
                  <td className="px-5 py-3 text-center"><div className="flex items-center justify-center gap-2"><CellBool v={r.ts} /></div></td>
                  <td className="px-5 py-3 text-center"><div className="flex items-center justify-center gap-2 opacity-70"><CellBool v={r.them} /></div></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* CTA */}
        <div className="mt-14 rounded-3xl bg-primary text-primary-foreground p-10 text-center relative overflow-hidden">
          <div className="absolute inset-0 grain opacity-40" />
          <div className="relative">
            <h2 className="font-display text-3xl md:text-4xl font-light">
              Save £{provider === "applecare" ? "181" : "166"} over 2 years.
            </h2>
            <p className="mt-2 text-primary-foreground/80">
              No signup gate. See your live quote in 10 seconds.
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Button
                size="lg"
                variant="secondary"
                className="bg-secondary text-teal hover:bg-secondary/90 rounded-full px-7"
                onClick={() => nav("/#instant-quote")}
                data-testid="compare-cta-quote"
              >
                Get instant quote <Zap className="w-4 h-4 ml-1" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="rounded-full px-7 border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10"
                onClick={() => nav("/repair-cost")}
                data-testid="compare-cta-estimator"
              >
                See what a repair costs <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        </div>

        <p className="mt-10 text-center text-xs text-muted-foreground max-w-3xl mx-auto">
          Prices sourced from {meta.name} public pricing and Treasurescare master data (Feb 2026).
          2-year total assumes 2 screen repairs + 1 accidental damage claim on a flagship device.
          Treasurescare is not affiliated with {meta.brand}. All trademarks belong to their respective owners.
        </p>
      </main>
    </div>
  );
}
